import React, { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import "../sass/components/modalConverterChamadosProjeto.scss";

const PRIORIDADES = ["BAIXA", "MEDIA", "ALTA", "CRITICA"];

const texto = (valor) => (valor == null ? "" : String(valor).trim());

const prioridadePorUrgencia = (urgencia) => {
  const valor = Number(urgencia);
  if (valor >= 4) return "CRITICA";
  if (valor === 3) return "ALTA";
  if (valor === 2) return "MEDIA";
  return "BAIXA";
};

const normalizarDevs = (payload) => {
  const lista = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.dados)
        ? payload.dados
        : [];

  return lista
    .map((item) => {
      const id = Number(
        item?.IdFuncionario ??
          item?.ID_FUNCIONARIO ??
          item?.ID_DEV ??
          item?.id ??
          item?.ID ??
          item?.CodFunc,
      );

      const nome = texto(
        item?.Nome ??
          item?.NOME ??
          item?.nome ??
          item?.RazaoSocial ??
          item?.Usuario ??
          item?.usuario,
      );

      return { id, nome };
    })
    .filter((item) => Number.isInteger(item.id) && item.id > 0 && item.nome)
    .filter(
      (item, index, arr) =>
        arr.findIndex((outro) => outro.id === item.id) === index,
    )
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
};

const ModalConverterChamadoProjeto = ({
  isOpen,
  onClose,
  chamado,
  funcionarioAdm,
  codFuncAdm,
  nivelAdm,
  onSuccess,
}) => {
  const idChamado = Number(chamado?.id ?? chamado?.ID ?? 0);
  const cliente =
    texto(chamado?.RazaoSocial) ||
    texto(chamado?.NomeFant) ||
    texto(chamado?.chvCad ?? chamado?.ChvCad) ||
    "Cliente não identificado";

  const tituloPadrao = useMemo(
    () => `Chamado #${idChamado || "-"} - ${cliente}`.slice(0, 120),
    [idChamado, cliente],
  );

  const [devs, setDevs] = useState([]);
  const [carregandoDevs, setCarregandoDevs] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroLocal, setErroLocal] = useState("");
  const [form, setForm] = useState({
    devId: "",
    titulo: tituloPadrao,
    descricaoProjeto: "",
    prioridade: prioridadePorUrgencia(chamado?.urgencia),
    dtPrevista: "",
    ordem: "",
    transferirFoco: false,
    justificativa: "",
  });

  useEffect(() => {
    if (!isOpen) return;

    setForm({
      devId: "",
      titulo: tituloPadrao,
      descricaoProjeto: texto(chamado?.DescrAtend ?? chamado?.descrAtend),
      prioridade: prioridadePorUrgencia(chamado?.urgencia),
      dtPrevista: "",
      ordem: "",
      transferirFoco: false,
      justificativa:
        "O atendimento identificou necessidade de alteração no sistema.",
    });
    setErroLocal("");
  }, [isOpen, chamado, tituloPadrao]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const fecharEsc = (event) => {
      if (event.key === "Escape" && !enviando) onClose?.();
    };

    window.addEventListener("keydown", fecharEsc);
    return () => window.removeEventListener("keydown", fecharEsc);
  }, [isOpen, enviando, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    let ativo = true;

    const carregar = async () => {
      setCarregandoDevs(true);

      const tentativas = [
        "/func/setor/PROGRAMA%C3%87%C3%83O",
        "/func/setor/PROGRAMACAO",
      ];

      try {
        for (const rota of tentativas) {
          try {
            const { data } = await api.get(rota, {
              params: { situacao: "Ativo", bloqueado: 0 },
            });
            const lista = normalizarDevs(data);
            if (lista.length) {
              if (ativo) setDevs(lista);
              return;
            }
          } catch (_) {
            // Tenta a próxima rota por compatibilidade.
          }
        }

        const { data } = await api.get("/loginUser");
        if (ativo) setDevs(normalizarDevs(data));
      } catch (error) {
        console.error("Erro ao carregar desenvolvedores:", error);
        if (ativo) setDevs([]);
      } finally {
        if (ativo) setCarregandoDevs(false);
      }
    };

    carregar();

    return () => {
      ativo = false;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const alterar = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
    setErroLocal("");
  };

  const enviar = async (event) => {
    event.preventDefault();
    if (enviando) return;

    if (!idChamado) {
      setErroLocal("Chamado inválido.");
      return;
    }

    if (!Number(form.devId)) {
      setErroLocal("Selecione o desenvolvedor responsável.");
      return;
    }

    if (!texto(form.titulo)) {
      setErroLocal("Informe o título do projeto.");
      return;
    }

    if (!texto(form.justificativa)) {
      setErroLocal("Informe o motivo da conversão.");
      return;
    }

    setEnviando(true);

    try {
      const payload = {
        funcionarioAdm: texto(funcionarioAdm),
        codFuncAdm: texto(codFuncAdm),
        nivelAdm: texto(nivelAdm) || "ADM",
        devId: Number(form.devId),
        titulo: texto(form.titulo),
        descricaoProjeto: texto(form.descricaoProjeto),
        prioridade: form.prioridade,
        dtPrevista: form.dtPrevista || null,
        ordem: form.ordem ? Number(form.ordem) : null,
        transferirFoco: Boolean(form.transferirFoco),
        justificativa: texto(form.justificativa),
      };

      const { data } = await api.post(
        `/chamados/converter-projeto/${idChamado}`,
        payload,
      );

      showInfo(
        data?.message ||
          `Chamado convertido no projeto ${data?.projeto?.CODIGO || "criado"}.`,
      );
      await onSuccess?.(data);
      onClose?.();
    } catch (error) {
      const mensagem =
        error?.response?.data?.message ||
        error?.message ||
        "Falha ao converter o chamado em projeto.";
      setErroLocal(mensagem);
      showError(mensagem);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="mcp-backdrop" onMouseDown={onClose}>
      <section
        className="mcp-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mcp-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="mcp-header">
          <div>
            <span className="mcp-eyebrow">Fluxo administrativo</span>
            <h2 id="mcp-title">Converter chamado em projeto</h2>
            <p>O chamado será encerrado e o projeto entrará na fila do DEV.</p>
          </div>
          <button
            type="button"
            className="mcp-close"
            onClick={onClose}
            disabled={enviando}
            aria-label="Fechar"
          >
            ×
          </button>
        </header>

        <div className="mcp-source-card">
          <div>
            <span>Chamado de origem</span>
            <strong>#{idChamado}</strong>
          </div>
          <div>
            <span>Cliente</span>
            <strong>{cliente}</strong>
          </div>
          <div>
            <span>Licença</span>
            <strong>{texto(chamado?.chvCad ?? chamado?.ChvCad) || "—"}</strong>
          </div>
        </div>

        <div className="mcp-warning">
          <strong>Atenção operacional</strong>
          <span>
            Após confirmar, o chamado deixa as filas de atendimento. O vínculo
            com o projeto será preservado pelo número do chamado.
          </span>
        </div>

        <form className="mcp-form" onSubmit={enviar}>
          <div className="mcp-grid">
            <label className="mcp-field mcp-col-2">
              <span>Título do projeto *</span>
              <input
                value={form.titulo}
                maxLength={120}
                onChange={(event) => alterar("titulo", event.target.value)}
              />
            </label>

            <label className="mcp-field">
              <span>Desenvolvedor responsável *</span>
              <select
                value={form.devId}
                onChange={(event) => alterar("devId", event.target.value)}
                disabled={carregandoDevs}
              >
                <option value="">
                  {carregandoDevs
                    ? "Carregando desenvolvedores..."
                    : "Selecione o responsável"}
                </option>
                {devs.map((dev) => (
                  <option key={dev.id} value={dev.id}>
                    {dev.nome}
                  </option>
                ))}
              </select>
              {!carregandoDevs && devs.length === 0 ? (
                <small>
                  Nenhum DEV retornado. Confirme o cadastro do setor PROGRAMAÇÃO.
                </small>
              ) : null}
            </label>

            <label className="mcp-field">
              <span>Prioridade *</span>
              <select
                value={form.prioridade}
                onChange={(event) => alterar("prioridade", event.target.value)}
              >
                {PRIORIDADES.map((prioridade) => (
                  <option key={prioridade} value={prioridade}>
                    {prioridade}
                  </option>
                ))}
              </select>
            </label>

            <label className="mcp-field">
              <span>Previsão</span>
              <input
                type="datetime-local"
                value={form.dtPrevista}
                onChange={(event) => alterar("dtPrevista", event.target.value)}
              />
            </label>

            <label className="mcp-field">
              <span>Posição na fila</span>
              <input
                type="number"
                min="1"
                value={form.ordem}
                placeholder="Automática"
                onChange={(event) => alterar("ordem", event.target.value)}
              />
            </label>

            <label className="mcp-field mcp-col-2">
              <span>Complemento técnico</span>
              <textarea
                rows={4}
                maxLength={2000}
                value={form.descricaoProjeto}
                onChange={(event) =>
                  alterar("descricaoProjeto", event.target.value)
                }
                placeholder="Contexto técnico, cenário, evidências e resultado esperado."
              />
            </label>

            <label className="mcp-field mcp-col-2">
              <span>Motivo da conversão *</span>
              <textarea
                rows={3}
                maxLength={500}
                value={form.justificativa}
                onChange={(event) =>
                  alterar("justificativa", event.target.value)
                }
              />
            </label>
          </div>

          <label className="mcp-focus-check">
            <input
              type="checkbox"
              checked={form.transferirFoco}
              onChange={(event) =>
                alterar("transferirFoco", event.target.checked)
              }
            />
            <span>
              Definir este projeto como foco atual do desenvolvedor selecionado
            </span>
          </label>

          {erroLocal ? <div className="mcp-error">{erroLocal}</div> : null}

          <footer className="mcp-footer">
            <button
              type="button"
              className="mcp-btn mcp-btn-secondary"
              onClick={onClose}
              disabled={enviando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="mcp-btn mcp-btn-primary"
              disabled={enviando || carregandoDevs}
            >
              {enviando ? "Convertendo..." : "Converter e atribuir"}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};

export default ModalConverterChamadoProjeto;
